import { useState, useEffect, useRef, useMemo } from 'react';
import { getTimeRemaining } from '../utils/timeHelper';

export const useCountdownTimers = (topics, onTimerExpired) => {
  const [timers, setTimers] = useState({});
  const intervalRef = useRef(null);
  const previousTimersRef = useRef({});

  // Memoize topics that need active timers (not expired)
  const activeTopics = useMemo(() => {
    return topics.filter(topic => {
      if (!topic.crawl_end_at) return false;
      const timeData = getTimeRemaining(topic.crawl_end_at);
      return timeData && !timeData.expired;
    });
  }, [topics]);

  useEffect(() => {
    const calculateTimers = () => {
      const newTimers = {};
      
      // Update all topics (both active and expired)
      topics.forEach(topic => {
        if (topic.crawl_end_at) {
          newTimers[topic._id] = getTimeRemaining(topic.crawl_end_at);
        }
      });
      
      return newTimers;
    };

    // Set initial timers
    const initialTimers = calculateTimers();
    setTimers(initialTimers);
    previousTimersRef.current = initialTimers;

    // Only set interval if there are active timers
    if (activeTopics.length > 0) {
      intervalRef.current = setInterval(() => {
        const updatedTimers = calculateTimers();
        
        // Check for newly expired timers
        Object.keys(updatedTimers).forEach(topicId => {
          const previous = previousTimersRef.current[topicId];
          const current = updatedTimers[topicId];
          
          // Detect transition from active to expired
          if (previous && !previous.expired && current && current.expired) {
            // Timer just expired! Trigger callback
            if (onTimerExpired) {
              onTimerExpired(topicId);
            }
          }
        });
        
        setTimers(updatedTimers);
        previousTimersRef.current = updatedTimers;
      }, 1000);
    }

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [topics, activeTopics, onTimerExpired]);

  return timers;
};